import { Auth } from "aws-amplify";
import axios from "axios";

const { baseURL } = require("../config");

/**
 * The login function is an asynchronous function that takes an email and password as parameters, sends
 * a POST request to a specific endpoint with the email and password as the payload, and returns an
 * object with an error flag and the response data if successful, or an error flag and the error
 * message if unsuccessful.
 * @param email - The `email` parameter is the email address of the user trying to log in.
 * @param password - The `password` parameter is the password that the user is trying to use to log in
 * to their account.
 * @returns The login function is returning an object with two properties: "error" and "data". If the
 * login is successful, the "error" property will be set to false and the "data" property will contain
 * the response data from the server. If there is an error during the login process, the "error"
 * property will be set to true and the "message" property will contain the error message
 */
async function login(email, password) {
    let payload = {
        email: email,
        password
    };
    // payload = JSON.stringify(payload);
    try {
        const data = await axios.post(`/facility-users/log-in`, payload);
        return {error: false, data: data.data};
    } catch (error) {
        return {error: true, message: error};
    }
}
/**
 * The `forgotPassword` function is an asynchronous function that sends a POST request to a specified
 * URL with an email and payload, and returns an object with an error flag and data if successful, or
 * an error flag and message if unsuccessful.
 * @param email - The `email` parameter is a string that represents the email address of the user who
 * forgot their password.
 * @param payload - The `payload` parameter is an object that contains the necessary data for the
 * forgot password request. It could include information such as the user's username, password, and any
 * additional data required for the password reset process.
 * @returns an object with two properties: "error" and "data" or "message". If the request is
 * successful, the "error" property will be set to false and the "data" property will contain the
 * response data. If there is an error, the "error" property will be set to true and the "message"
 * property will contain the error message.
 */
async function forgotPassword(email, payload) {
    // payload = JSON.stringify(payload);
    try {
        const data = await axios.post(`${baseURL}${email}`, payload);
        return {error: false, data: data.data};
    } catch (error) {
        return {error: true, message: error};
    }
}
/**
 * The function `BranchGet` is an asynchronous function that makes a GET request to the Branch API
 * using a given link and key, and returns the response data or an error message.
 * @param link - The `link` parameter is the URL of the branch you want to retrieve information for. It
 * is a string that represents the URL of the branch.
 * @param key - The `key` parameter is the Branch API key. It is a unique identifier that is used to
 * authenticate and authorize access to the Branch API. You need to provide a valid Branch API key in
 * order to make requests to the Branch API.
 * @returns an object with two properties: "error" and "data". If there is no error, the "error"
 * property will be set to false and the "data" property will contain the response data from the API
 * call. If there is an error, the "error" property will be set to true and the "message" property will
 * contain the error message.
 */
async function BranchGet(link , key) {
    let payload = {};
    // payload = JSON.stringify(payload);
    try {
        const data = await axios.get(`https://api2.branch.io/v1/url?url=${link}&branch_key=${key}`, payload);
        return {error: false, data: data.data};
    } catch (error) {
        return {error: true, message: error};
    }
}
/**
 * The `resetPassword` function is an asynchronous function that sends a PATCH request to a specified
 * endpoint with a payload, using the `axios` library, and returns an object with the response data or
 * an error message.
 * @param endpoint - The `endpoint` parameter is the URL endpoint where the password reset request will
 * be sent. It should be a string representing the specific API endpoint for resetting the password.
 * @param payload - The `payload` parameter is an object that contains the data needed to reset the
 * password. It could include properties such as the user's email, new password, and any other
 * necessary information for the password reset process.
 * @returns an object with two properties: "error" and "data". If there is no error, the "error"
 * property will be set to false and the "data" property will contain the response data from the API
 * call. If there is an error, the "error" property will be set to true and the "message" property will
 * contain the error message.
 */
async function resetPassword(endpoint,payload) {
    // payload = JSON.stringify(payload);
    const idToken = await Auth.currentSession();
    const id = idToken.idToken.payload["custom:id"];
    const headers = {
        Authorization: `Bearer ${idToken.getIdToken().getJwtToken()}`,
    };
    try {
        const data = await axios.patch(`${baseURL}${endpoint}`, payload,{
            headers: headers,
        });
        return {error: false, data: data};
    } catch (error) {
        return {error: true, message: error};
    }
}
export const userService = {
    login,
    resetPassword,
    forgotPassword,
    BranchGet
}
